/* cspell:ignore mailv */
// Sendcloud's `code` is stable; its display `name` ("DPD UK") is not. Carrier
// governance keys off these labels, so always resolve by code.
const SENDCLOUD_CARRIER_CODE_LABELS = {
  royal_mail: "Royal Mail",
  royal_mailv2: "Royal Mail",
  inpost: "InPost",
  inpost_gb: "InPost",
  inpost_pl: "InPost",
  inpost_global: "InPost",
  dpd: "DPD",
  dpd_gb: "DPD",
  colissimo: "Colissimo",
  chronopost: "Chronopost",
  postnl: "PostNL",
  correos: "Correos",
  correos_express: "Correos Express",
  evri: "Evri",
  hermes_gb: "Evri",
  mondial_relay: "Mondial Relay",
  gls: "GLS",
  gls_eu: "GLS",
  bpost: "bpost",
  dhl: "DHL",
  poste_it_delivery: "Poste Italiane",
  ups: "UPS",
  fedex: "FedEx",
} as const satisfies Record<string, string>;

export type SendcloudCarrierCode = keyof typeof SENDCLOUD_CARRIER_CODE_LABELS;

/** Canonical `rate.carrier` value for a Sendcloud carrier. */
export type SendcloudCarrierLabel =
  (typeof SENDCLOUD_CARRIER_CODE_LABELS)[SendcloudCarrierCode];

/** Every carrier identity Sendcloud can quote, deduplicated, in code order. */
export const SENDCLOUD_CARRIER_LABELS: readonly SendcloudCarrierLabel[] = [
  ...new Set(Object.values(SENDCLOUD_CARRIER_CODE_LABELS)),
];

const SENDCLOUD_NAME_SUFFIX = /\s+by\s+sendcloud\s*$/i;

// Widened so an arbitrary runtime string can be looked up without an assertion.
const CODE_LABELS: Readonly<Record<string, SendcloudCarrierLabel | undefined>> =
  SENDCLOUD_CARRIER_CODE_LABELS;

const labelFor = (value: string): SendcloudCarrierLabel | undefined =>
  CODE_LABELS[value.trim().toLowerCase()];

/** False for a carrier Sendcloud quotes that we have not registered yet. */
export function isKnownSendcloudCarrier(code: string, name?: string): boolean {
  if (labelFor(code)) {
    return true;
  }
  return (
    name !== undefined &&
    labelFor(name.replace(SENDCLOUD_NAME_SUFFIX, "")) !== undefined
  );
}

/**
 * "royal_mailv2" -> "Royal Mail". Prefers the stable code; an unregistered code
 * falls back to its display name, then to the raw value, so a carrier Sendcloud
 * adds still quotes rather than disappearing.
 */
export function prettySendcloudCarrierName(
  code: string,
  name?: string,
): string {
  const fromCode = labelFor(code);
  if (fromCode) {
    return fromCode;
  }
  const fallback = (name ?? code).replace(SENDCLOUD_NAME_SUFFIX, "").trim();
  return labelFor(fallback) ?? fallback;
}
